import { createContext, useContext, useState, useEffect, useCallback } from 'react';
import axios from 'axios';

const AuthContext = createContext(null);
const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Set default Authorization header if token exists in localStorage
const setupAxiosAuth = (token) => {
  if (token) {
    axios.defaults.headers.common['Authorization'] = `Bearer ${token}`;
    localStorage.setItem('access_token', token);
  } else {
    delete axios.defaults.headers.common['Authorization'];
    localStorage.removeItem('access_token');
  }
};

// Initialize from localStorage on load
const savedToken = localStorage.getItem('access_token');
if (savedToken) {
  axios.defaults.headers.common['Authorization'] = `Bearer ${savedToken}`;
}

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  const checkAuth = useCallback(async () => {
    const token = localStorage.getItem('access_token');
    if (!token) {
      setUser(null);
      setLoading(false);
      return;
    }
    try {
      const { data } = await axios.get(`${API}/auth/me`, { withCredentials: true });
      setUser(data);
    } catch {
      setupAxiosAuth(null);
      setUser(null);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    checkAuth();
  }, [checkAuth]);

  const login = async (email, password) => {
    const { data } = await axios.post(`${API}/auth/login`, { email, password }, { withCredentials: true });
    setupAxiosAuth(data.access_token);
    setUser(data.user);
    return data;
  };

  const register = async (name, email, password, phone) => {
    const { data } = await axios.post(`${API}/auth/register`, { name, email, password, phone });
    return data;
  };

  const verifyOtp = async (email, otp) => {
    const { data } = await axios.post(`${API}/auth/verify-otp`, { email, otp });
    return data;
  };

  const resendOtp = async (email) => {
    const { data } = await axios.post(`${API}/auth/resend-otp`, { email });
    return data;
  };

  const updateProfile = async (profileData) => {
    const { data } = await axios.put(`${API}/auth/profile`, profileData, { withCredentials: true });
    setUser(data.user);
    return data;
  };

  const logout = async () => {
    try {
      await axios.post(`${API}/auth/logout`, {}, { withCredentials: true });
    } catch {}
    setupAxiosAuth(null);
    setUser(null);
  };

  const changePassword = async (oldPassword, newPassword) => {
    const { data } = await axios.post(`${API}/auth/change-password`, { old_password: oldPassword, new_password: newPassword }, { withCredentials: true });
    return data;
  };

  const forgotPassword = async (email) => {
    const { data } = await axios.post(`${API}/auth/forgot-password`, { email });
    return data;
  };

  const resetPassword = async (email, otp, newPassword) => {
    const { data } = await axios.post(`${API}/auth/reset-password`, { email, otp, new_password: newPassword });
    return data;
  };

  return (
    <AuthContext.Provider value={{ user, loading, login, register, verifyOtp, resendOtp, updateProfile, logout, changePassword, forgotPassword, resetPassword, checkAuth, setUser }}>
      {children}
    </AuthContext.Provider>
  );
}

export const useAuth = () => useContext(AuthContext);

